/**
 * Package Store API client.
 * Follows pattern from plugins/api.js
 */
import { PackageData, StagedPackageData } from './types';
import { handleApiResponse } from '../common';
import { getApiBaseUrl, API_VERSION } from '../index';

export const packagesApi = {
  /**
   * Sync packages from remote store
   */
  async sync() {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/packages/sync`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      const validatedResponse = await handleApiResponse(response);
      return validatedResponse.json();
    } catch (error) {
      console.error('Failed to sync packages:', error);
      throw error;
    }
  },

  /**
   * List all remote packages
   * @param {Object} options
   * @param {string} options.type - Filter by type ('app_launcher' or 'tool')
   */
  async listRemote({ type = null } = {}) {
    try {
      const params = type ? `?type=${type}` : '';
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/packages/remote${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();

      return {
        packages: PackageData.fromApiResponseList(data.packages || []),
        total: data.total || 0,
        last_sync: data.last_sync
      };
    } catch (error) {
      console.error('Failed to fetch remote packages:', error);
      throw error;
    }
  },

  /**
   * Get a specific remote package
   */
  async getRemote(packageId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/packages/remote/${packageId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return PackageData.fromApiResponse(data);
    } catch (error) {
      console.error(`Failed to fetch package ${packageId}:`, error);
      throw error;
    }
  },

  /**
   * List staged packages
   */
  async listStaged() {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/packages/staged`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();

      return {
        staged: StagedPackageData.fromApiResponseList(data.staged || []),
        total: data.total || 0
      };
    } catch (error) {
      console.error('Failed to fetch staged packages:', error);
      throw error;
    }
  },

  /**
   * Stage a package
   */
  async stage(packageId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/packages/staged`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ package_id: packageId })
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return StagedPackageData.fromApiResponse(data);
    } catch (error) {
      console.error(`Failed to stage package ${packageId}:`, error);
      throw error;
    }
  },

  /**
   * Remove a package from staging
   */
  async unstage(stagedId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/packages/staged/${stagedId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      await handleApiResponse(response);
      return true;
    } catch (error) {
      console.error(`Failed to unstage package ${stagedId}:`, error);
      throw error;
    }
  },

  /**
   * Clear all staged packages
   */
  async clearStaging() {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/packages/staged`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const validatedResponse = await handleApiResponse(response);
      return validatedResponse.json();
    } catch (error) {
      console.error('Failed to clear staging:', error);
      throw error;
    }
  },

  // =====================================================
  // INSTALL ENDPOINTS (Phase 3.2)
  // =====================================================

  /**
   * Get features (tools) associated with a launcher
   * @param {string} storeId - Store UUID of the launcher
   * @returns {Object} { launcher_id, launcher_slug, features: [{id, slug, name, ...}] }
   */
  async getLauncherFeatures(storeId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/install/launcher-features/${storeId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      const validatedResponse = await handleApiResponse(response);
      return validatedResponse.json();
    } catch (error) {
      console.error(`Failed to get launcher features ${storeId}:`, error);
      throw error;
    }
  },

  /**
   * Install a launcher package
   * @param {string} storeId - Store UUID of the launcher
   * @param {Object} options - Installation options
   * @param {Object} options.config - User-filled configuration_fields
   * @param {Object} options.env_vars - Launcher-level environment variables
   * @param {Array} options.selected_versions - Versions to install profiles for
   * @param {Object} options.profile_env_vars - Per-version env vars {"2024": {...}}
   * @returns {Object} Install result with clone instructions
   */
  async installLauncher(storeId, options = {}) {
    const { config = {}, env_vars = {}, selected_versions = [], profile_env_vars = {} } = options;
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/install/launcher`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ 
          store_id: storeId, 
          config,
          env_vars,
          selected_versions,
          profile_env_vars
        })
      });

      const validatedResponse = await handleApiResponse(response);
      return validatedResponse.json();
    } catch (error) {
      console.error(`Failed to install launcher ${storeId}:`, error);
      throw error;
    }
  },

  /**
   * Install a tool package
   * @param {string} storeId - Store UUID of the tool
   * @param {Object} options - Installation options
   * @param {Object} options.config - User-filled configuration_fields
   * @returns {Object} Install result with clone instructions
   */
  async installTool(storeId, options = {}) {
    const { config = {} } = options;
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/install/tool`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ store_id: storeId, config })
      });

      const validatedResponse = await handleApiResponse(response);
      return validatedResponse.json();
    } catch (error) {
      console.error(`Failed to install tool ${storeId}:`, error);
      throw error;
    }
  },

  /**
   * List installed launchers
   */
  async listInstalledLaunchers() {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/install/launchers`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      const validatedResponse = await handleApiResponse(response);
      return validatedResponse.json();
    } catch (error) {
      console.error('Failed to list installed launchers:', error);
      throw error;
    }
  },

  /**
   * List installed tools
   */
  async listInstalledTools() {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/install/tools`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      const validatedResponse = await handleApiResponse(response);
      return validatedResponse.json();
    } catch (error) {
      console.error('Failed to list installed tools:', error);
      throw error;
    }
  },

  /**
   * Get tools for a specific launcher (for menu discovery)
   * @param {number} launcherId - Local launcher ID
   */
  async getToolsForLauncher(launcherId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/install/tools/for-launcher/${launcherId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      const validatedResponse = await handleApiResponse(response);
      return validatedResponse.json();
    } catch (error) {
      console.error(`Failed to get tools for launcher ${launcherId}:`, error);
      throw error;
    }
  }
};

